import { inject, Injectable } from "@angular/core";

import { TransactionLinkType } from "../../../models/types/transaction.types";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { TransactionStoreService } from "../../store/transaction/transaction.store.service";

import { TransactionConversionService } from "./transaction-conversion.service";

/**
 * Manual (user-driven) transfer/conversion link management. Auto-detection lives
 * in {@link TransactionConversionService}; this service owns the deliberate
 * link/unlink actions surfaced in the UI and their vault persistence.
 */
@Injectable({
  providedIn: "root",
})
export class TransactionLinkService {
  private transactionStore = inject(TransactionStoreService);
  private conversionService = inject(TransactionConversionService);

  /**
   * Plausible partners for a manual link: every other transaction that forms a
   * valid transfer/conversion with `source` under the same predicate
   * auto-detection uses. The time window is intentionally not applied - a
   * deliberate link may join transactions further apart than auto-detection
   * would pair (that is the main case auto-detection leaves for the user).
   * Transactions already linked (persisted or auto-detected) are excluded so a
   * partner is never double-booked.
   */
  getCandidates(source: TransactionView): TransactionView[] {
    const all = this.transactionStore.enrichTransaction.collection() ?? [];
    return all.filter(
      (candidate) =>
        candidate.id !== source.id &&
        !this.isLinked(candidate) &&
        this.conversionService.getLinkType(source, candidate) !== null,
    );
  }

  /**
   * Deliberately links a user-chosen pair. Validates via the auto-detection
   * predicate; on a valid pair it writes `linkedTo` on both sides, drops any
   * stale exclusion between them (the user changed their mind about a pair they
   * previously unlinked) and persists both in a single vault save.
   *
   * Returns the link type on success, or null (writing nothing) when the pair is
   * not a valid transfer/conversion, either side is no longer a persisted
   * transaction (a synthetic empty-account balance, or one deleted while the
   * picker stayed open — re-saving it would forge a cipher), or either side has
   * been linked since the picker listed it (a second click or a concurrent link;
   * a third partner can't be represented). A failed vault write rejects rather
   * than returning null, so the caller can surface it instead of a false success.
   */
  async linkPair(a: TransactionView, b: TransactionView): Promise<TransactionLinkType | null> {
    // Store views are frozen; resolve the persisted sources and mutate clones.
    const persisted = this.transactionStore.transaction.transactionViews() ?? [];
    const aSource = persisted.find((t) => t.id === a.id);
    const bSource = persisted.find((t) => t.id === b.id);
    if (!aSource || !bSource || this.isLinked(aSource) || this.isLinked(bSource)) {
      return null;
    }

    const linkType = this.conversionService.getLinkType(aSource, bSource);
    if (!linkType) {
      return null;
    }

    const aClone = aSource.clone();
    const bClone = bSource.clone();
    this.forgeLink(aClone, bClone);
    this.forgeLink(bClone, aClone);

    await this.transactionStore.transaction.saveToVault([aClone, bClone], true);
    return linkType;
  }

  /** A transaction already carrying a persisted or auto-detected link. */
  private isLinked(transaction: TransactionView): boolean {
    return (transaction.linkedTo?.length ?? 0) > 0 || (transaction.c_linkedTo?.length ?? 0) > 0;
  }

  /**
   * Records a deliberate link to `partner` and removes any exclusion of it, so
   * auto-detection respects the link and never treats the pair as vetoed.
   */
  private forgeLink(view: TransactionView, partner: TransactionView): void {
    if (!(view.linkedTo ?? []).includes(partner.id)) {
      view.linkedTo = [...(view.linkedTo ?? []), partner.id];
    }
    view.excludedLinkIds = view.excludedLinkIds.filter((id) => id !== partner.id);
  }

  /**
   * Breaks the transfer/conversion link on a transaction and every partner it is
   * linked to, whether the link is persisted (`linkedTo`) or auto-detected
   * (`c_linkedTo`).
   *
   * On both sides it clears the link and records the partner in
   * `excludedLinkIds` (`xLid`), so auto-detection never re-links the pair on the
   * next enrichment cycle (the exclusion is the durable half — for an
   * auto-detected-only pair there is no persisted `linkedTo` to clear, but the
   * exclusion still sticks). Every touched transaction is written in a single
   * vault save.
   *
   * Returns `true` when the unlink was applied (or there was nothing to unlink),
   * and `false` only when the transaction is no longer in the store — it was
   * deleted elsewhere while the dialog stayed open, so nothing was persisted.
   * The caller uses that to distinguish a real unlink from a silent no-op it
   * must surface to the user.
   */
  async unlink(transaction: TransactionView): Promise<boolean> {
    const partnerIds = this.partnerIdsOf(transaction);
    if (!partnerIds.length) {
      return true;
    }

    // Store views are frozen; resolve the persisted source and mutate clones.
    const persisted = this.transactionStore.transaction.transactionViews() ?? [];
    const current = persisted.find((t) => t.id === transaction.id)?.clone();
    if (!current) {
      // The transaction was deleted from the store while the dialog stayed open;
      // re-saving a stale snapshot would emit no `del` flag and resurrect the
      // soft-deleted cipher. Report that nothing was unlinked rather than persist
      // a view that no longer exists.
      return false;
    }

    const toSave: TransactionView[] = [current];
    for (const partnerId of partnerIds) {
      this.severLink(current, partnerId);

      const partnerSource = persisted.find((t) => t.id === partnerId);
      if (partnerSource) {
        const partner = partnerSource.clone();
        this.severLink(partner, current.id);
        toSave.push(partner);
      }
    }

    await this.transactionStore.transaction.saveToVault(toSave, true);
    return true;
  }

  /** Unique partner ids drawn from both persisted and auto-detected links. */
  private partnerIdsOf(transaction: TransactionView): string[] {
    return Array.from(
      new Set([...(transaction.linkedTo ?? []), ...(transaction.c_linkedTo ?? [])]),
    );
  }

  /**
   * Removes `partnerId` from a transaction's persisted links and records it as
   * an exclusion so it is never auto-relinked. Only `linkedTo` and
   * `excludedLinkIds` are persisted — `toStoreModel` omits the enriched `c_`
   * fields, and the badge refreshes from the re-enriched store after the save,
   * so a clone's `c_*` values never surface.
   */
  private severLink(view: TransactionView, partnerId: string): void {
    view.linkedTo = (view.linkedTo ?? []).filter((id) => id !== partnerId);
    if (!view.excludedLinkIds.includes(partnerId)) {
      view.excludedLinkIds = [...view.excludedLinkIds, partnerId];
    }
  }
}
